import React, { useState } from 'react';
import { useControlSpec } from '../contexts/ControlSpecContext';
import { X, ChevronDown, ChevronUp } from 'lucide-react';

const DetailedModal = ({ isOpen, onClose }) => {
    const { controlSpec, changeLog } = useControlSpec();
    const [expandedSteps, setExpandedSteps] = useState(new Set());

    if (!isOpen || !controlSpec) {
        return null;
    }

    const isStepChanged = (stepNumber) => {
        return changeLog.some(change =>
            change.includes(`step ${stepNumber}`) ||
            change.includes(`Step ${stepNumber}`)
        );
    };

    const toggleStep = (stepNumber) => {
        const newExpanded = new Set(expandedSteps);
        if (newExpanded.has(stepNumber)) {
            newExpanded.delete(stepNumber);
        } else {
            newExpanded.add(stepNumber);
        }
        setExpandedSteps(newExpanded);
    };

    const isExpanded = (stepNumber) => expandedSteps.has(stepNumber);

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black transition-opacity z-40 ${isOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'
                    }`}
                onClick={onClose}
            />

            {/* Sliding Modal */}
            <div
                className={`
          fixed top-0 right-0 h-full w-full md:w-2/3 lg:w-1/2 bg-white shadow-2xl
          transform transition-transform duration-300 ease-in-out z-50 overflow-hidden
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
            >
                <div className="h-full flex flex-col">
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{controlSpec.control_title}</h2>
                            <p className="text-sm text-gray-600 mt-1">{controlSpec.control_id} • {controlSpec.frequency}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500 hover:text-gray-700"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {/* Control Summary */}
                        <div className="mb-6">
                            <h3 className="text-sm font-semibold text-gray-700 mb-2">Control Objective</h3>
                            <p className="text-sm text-gray-600">{controlSpec.control_objective}</p>
                        </div>

                        {/* Scope Rules */}
                        {controlSpec.scope_rules && (
                            <div className="mb-6">
                                <h3 className="text-sm font-semibold text-gray-700 mb-2">Scope</h3>
                                <p className="text-sm text-gray-600">{controlSpec.scope_rules.population_source}</p>
                                {controlSpec.scope_rules.filters && controlSpec.scope_rules.filters.length > 0 && (
                                    <ul className="list-disc list-inside text-sm text-gray-600 mt-2 space-y-1">
                                        {controlSpec.scope_rules.filters.map((filter, i) => (
                                            <li key={i}>{filter}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        {/* Test Steps - Detailed */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-semibold text-gray-700">Test Steps</h3>
                            {controlSpec.test_steps?.map(step => {
                                const isChanged = isStepChanged(step.step_number);
                                const expanded = isExpanded(step.step_number);

                                return (
                                    <div
                                        key={step.step_number}
                                        className={`
                      border rounded-lg transition-all
                      ${isChanged
                                                ? 'border-yellow-400 bg-yellow-50 shadow-md'
                                                : 'border-gray-200 bg-white'
                                            }
                    `}
                                    >
                                        {/* Step Header - Always Visible */}
                                        <div
                                            onClick={() => toggleStep(step.step_number)}
                                            className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex justify-between items-start mb-2">
                                                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                                    Step {step.step_number}
                                                    {isChanged && (
                                                        <span className="text-xs bg-yellow-400 text-yellow-900 px-2 py-1 rounded">
                                                            UPDATED
                                                        </span>
                                                    )}
                                                </h4>
                                                {expanded ? (
                                                    <ChevronUp className="h-5 w-5 text-gray-400" />
                                                ) : (
                                                    <ChevronDown className="h-5 w-5 text-gray-400" />
                                                )}
                                            </div>

                                            <p className="text-sm text-gray-700 mb-3">{step.summary || step.description?.substring(0, 150) + '...'}</p>

                                            <div className="flex gap-4 text-sm">
                                                <span className="text-gray-600">
                                                    👤 <strong>{step.responsible_party}</strong>
                                                    {step.responsible_party_detail && ` → ${step.responsible_party_detail}`}
                                                </span>
                                                <span className="text-gray-600">
                                                    🔍 <strong>{step.audit_procedure}</strong>
                                                </span>
                                            </div>
                                        </div>

                                        {/* Expandable Details */}
                                        {expanded && (
                                            <div className="px-4 pb-4 border-t border-gray-200 pt-4 space-y-3">
                                                {/* Full Description */}
                                                <div>
                                                    <strong className="text-gray-700 text-sm">Full Description:</strong>
                                                    <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                                                </div>

                                                {/* Key Points */}
                                                {step.key_points && step.key_points.length > 0 && (
                                                    <div>
                                                        <strong className="text-gray-700 text-sm">Key Points:</strong>
                                                        <ul className="list-disc list-inside text-sm text-gray-600 mt-1 space-y-1">
                                                            {step.key_points.map((point, i) => (
                                                                <li key={i}>{point}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}

                                                {/* Required Evidence */}
                                                <div>
                                                    <strong className="text-gray-700 text-sm">Required Evidence:</strong>
                                                    <ul className="list-disc list-inside text-sm text-gray-600 mt-1 space-y-1">
                                                        {step.required_evidence?.map((ev, i) => (
                                                            <li key={i}>{ev}</li>
                                                        ))}
                                                    </ul>
                                                </div>

                                                {/* Expected Outcome */}
                                                {step.expected_outcome && (
                                                    <div>
                                                        <strong className="text-gray-700 text-sm">Expected Outcome:</strong>
                                                        <p className="text-sm text-gray-600 mt-1">{step.expected_outcome}</p>
                                                    </div>
                                                )}

                                                {/* Manual Control Fields */}
                                                {(step.control_dates || step.preparer_field_name || step.reviewer_field_name) && (
                                                    <div className="grid grid-cols-2 gap-3 text-sm pt-2 border-t border-gray-100">
                                                        {step.control_dates && (
                                                            <div>
                                                                <strong className="text-gray-600">Control Dates:</strong>
                                                                <p className="text-gray-500">{step.control_dates.join(', ')}</p>
                                                            </div>
                                                        )}
                                                        {step.preparer_field_name && (
                                                            <div>
                                                                <strong className="text-gray-600">Preparer Field:</strong>
                                                                <p className="text-gray-500">{step.preparer_field_name}</p>
                                                            </div>
                                                        )}
                                                        {step.reviewer_field_name && (
                                                            <div>
                                                                <strong className="text-gray-600">Reviewer Field:</strong>
                                                                <p className="text-gray-500">{step.reviewer_field_name}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Reviewer Expectations */}
                        {controlSpec.reviewer_expectations && (
                            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <h3 className="text-sm font-semibold text-blue-900 mb-2">Reviewer Expectations</h3>
                                <p className="text-sm text-blue-800">{controlSpec.reviewer_expectations}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default DetailedModal;
